import { shallowRef } from 'vue';
import * as Sentry from '@sentry/vue';
import http from '@/services/http';
import { isKnownTimeZone, wallClockAtOffset, wallClockInTimeZone } from '@/utils/customPlaylistSchedule';

/**
 * A zone's clock, read the way the players read it.
 *
 * A zone's time zone is `location_zones.time_zone_id`. The players use that
 * time zone's IANA name when it has one, and its name otherwise
 * (playercontrollers getprefs.php:28, `COALESCE(NULLIF(iana_name, ''), name)`).
 * The zone's search document only nests the time zone's display string, which
 * is its name. The two differ for 89 of 550 time zones, and for the Etc/GMT
 * rows the sign is flipped: row 485 is named Etc/GMT+5 but plays on Etc/GMT-5,
 * ten hours apart. So the time zones are read once from GET /api/time_zone and
 * looked up by id.
 *
 * Some names the players use are not ones a browser knows (America/Kentucky,
 * the Misc/* rows). For those, the time zone's fixed UTC offset stands in,
 * rather than the viewer's own clock.
 *
 * The list is cached for the session. Logging out calls {@link resetTimeZones}.
 */

interface TimeZoneRow {
    name: string | null;
    ianaName: string | null;
    /** Seconds east of UTC, as time_zones.offset stores it. */
    offsetSeconds: number | null;
}

/** What a zone contributes: its time zone id, and the name its document carries. */
export interface ZoneClockSource {
    timeZoneId?: number | null;
    timeZone?: string | null;
}

const byId = shallowRef<Map<number, TimeZoneRow> | null>(null);
let requested = false;
/** Bumped on reset, so a list that lands after a logout is dropped. */
let generation = 0;

function text(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

async function load(loadGeneration: number): Promise<void> {
    try {
        const response = await http.get('/api/time_zone');
        const rows = (response.data as { data?: Record<string, unknown>[] }).data ?? [];
        const next = new Map<number, TimeZoneRow>();
        for (const row of rows) {
            const id = Number(row.id);
            if (!Number.isInteger(id) || id <= 0) continue;
            next.set(id, {
                name: text(row.name),
                ianaName: text(row.iana_name),
                offsetSeconds: typeof row.offset === 'number' && Number.isFinite(row.offset) ? row.offset : null,
            });
        }
        if (loadGeneration === generation) byId.value = next;
    } catch (error) {
        // Reported so wrong clocks are visible. The zone names still stand in.
        Sentry.captureException(error);
    }
}

function timeZoneRow(id: number | null | undefined): TimeZoneRow | undefined {
    if (!requested) {
        requested = true;
        void load(generation);
    }
    return id != null ? byId.value?.get(id) : undefined;
}

/**
 * The zone's wall-clock time at `instant`, as a local Date whose fields read
 * the zone's clock, or null when there is nothing to place it with. A caller
 * with a fallback, such as the list's own zone, should use it on null.
 */
export function zoneWallClock(instant: Date, zone: ZoneClockSource | null | undefined): Date | null {
    if (!zone) return null;
    const row = timeZoneRow(zone.timeZoneId);
    for (const name of [row?.ianaName, row?.name, zone.timeZone]) {
        if (name && isKnownTimeZone(name)) return wallClockInTimeZone(instant, name);
    }
    if (row && row.offsetSeconds !== null) return wallClockAtOffset(instant, row.offsetSeconds);
    return null;
}

/** Forget the list. Called on logout, and by tests. */
export function resetTimeZones(): void {
    generation += 1;
    byId.value = null;
    requested = false;
}

export const __resetTimeZones = resetTimeZones;
